import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Text from 'antd/lib/typography/Text';
import { MoreOutlined } from '@ant-design/icons';

import { CombinedState } from 'reducers';
import { Reference } from 'cvat-core-wrapper';
import ActionsMenuContainer from 'containers/actions-menu/actions-reference-menu';
import Preview from 'components/common/preview';
import AutomaticAnnotationProgress from 'components/references-page/automatic-annotation-progress';
import { cancelInferenceAsyncRef } from 'actions/models-actions';
import { workspacePath } from 'utils/workspace-route';
import { plural, t } from 'utils/i18n';

interface Props {
    reference: Reference;
    // id → name of the tasks on the page, to say which task the photos belong to
    taskNames: Record<number, string>;
}

export function referenceLabel(photos: number, taskIds: number[], taskNames: Record<number, string>): string {
    const count = plural(photos, 'photo');
    if (!taskIds.length) return `${count} · ${t('not linked to a task')}`;
    return `${count} · ${taskIds.map((id) => taskNames[id] ?? t('Task #{id}', { id })).join(', ')}`;
}

// photos linked to a task open inside that task; unlinked ones open the legacy reference page
export function referencePath(reference: { id: number; taskId: number[] | null; jobs: { id: number }[] }): string {
    const [taskId] = reference.taskId ?? [];
    const [job] = reference.jobs;
    if (taskId && job) return workspacePath(taskId, job.id, reference.id);
    return `/references/${reference.id}`;
}

export default function ReferenceRow({ reference, taskNames }: Props): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch();
    const deletes = useSelector((state: CombinedState) => state.references.activities.deletes);
    const inference = useSelector((state: CombinedState) => state.models.inferencesRef[reference.id] ?? null);
    const deleted = reference.id in deletes ? deletes[reference.id] : false;
    const open = (): void => history.push(referencePath(reference));

    return (
        <div
            className={`cvat-project-task-row${deleted ? ' cvat-project-task-row-deleted' : ''}`}
            data-reference={reference.id}
        >
            <Preview
                reference={reference}
                loadingClassName='cvat-project-task-preview-loading'
                emptyPreviewClassName='cvat-project-task-preview-empty'
                previewWrapperClassName='cvat-project-task-preview'
                previewClassName='cvat-project-task-preview-image'
                onClick={open}
            />
            <div className='cvat-project-task-body'>
                <button type='button' className='cvat-project-task-title' onClick={open}>{reference.name}</button>
                <Text type='secondary'>{referenceLabel(reference.size, reference.taskId ?? [], taskNames)}</Text>
                <AutomaticAnnotationProgress
                    activeInference={inference}
                    cancelAutoAnnotation={() => dispatch(cancelInferenceAsyncRef(reference.id))}
                />
            </div>
            <Dropdown
                destroyPopupOnHide
                trigger={['click']}
                overlay={(
                    <ActionsMenuContainer
                        referenceInstance={reference}
                        onViewAnalytics={() => history.push(`/references/${reference.id}/analytics`)}
                    />
                )}
            >
                <Button type='text' size='small' aria-label={t('More')} icon={<MoreOutlined />} />
            </Dropdown>
        </div>
    );
}
